/**
 * Smart Script Loader for Conditional JavaScript Loading
 * Only loads scripts when components actually need them
 */

class ScriptLoader {
  constructor() {
    this.loadedScripts = new Set();
    this.loadingPromises = new Map();
    this.scriptCDNs = {
      jquery: '//cdnjs.cloudflare.com/ajax/libs/jquery/3.4.1/jquery.min.js',
      swiper: '//cdnjs.cloudflare.com/ajax/libs/Swiper/4.2.2/js/swiper.min.js',
      gsap: '//cdnjs.cloudflare.com/ajax/libs/gsap/3.12.2/gsap.min.js'
    };
  }

  /**
   * Load a script conditionally - only if not already loaded
   * @param {string} scriptName - Name of the script (jquery, swiper, etc.)
   * @param {string} customUrl - Optional custom URL override
   * @returns {Promise} Promise that resolves when script is loaded
   */
  async loadScript(scriptName, customUrl = null) {
    const url = customUrl || this.scriptCDNs[scriptName];
    
    if (!url) {
      throw new Error(`Unknown script: ${scriptName}`);
    }

    // If already loaded, return immediately
    if (this.loadedScripts.has(scriptName)) {
      return Promise.resolve();
    }

    // If currently loading, return existing promise
    if (this.loadingPromises.has(scriptName)) {
      return this.loadingPromises.get(scriptName);
    }

    // Create new loading promise
    const loadingPromise = this.createScriptElement(url, scriptName);
    this.loadingPromises.set(scriptName, loadingPromise);

    try {
      await loadingPromise;
      this.loadedScripts.add(scriptName);
      this.loadingPromises.delete(scriptName);
      console.log(`✅ Script loaded: ${scriptName}`);
    } catch (error) {
      this.loadingPromises.delete(scriptName);
      console.error(`❌ Failed to load script: ${scriptName}`, error);
      throw error;
    }

    return loadingPromise;
  }

  /**
   * Create and inject script element
   * @param {string} url - Script URL
   * @param {string} scriptName - Script identifier
   * @returns {Promise} Promise that resolves when script loads
   */
  createScriptElement(url, scriptName) {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.src = url;
      
      script.onload = () => {
        console.log(`📦 ${scriptName} loaded successfully`);
        resolve();
      };
      
      script.onerror = () => {
        console.error(`🚫 Failed to load ${scriptName} from ${url}`);
        reject(new Error(`Failed to load ${scriptName}`));
      };

      // Add to head
      document.head.appendChild(script);
    });
  }

  /**
   * Load jQuery only when needed
   * Sets up global $ and jQuery variables
   * @returns {Promise} Promise that resolves when jQuery is ready
   */
  async loadJQuery() {
    if (window.jQuery && window.$) {
      return Promise.resolve(window.jQuery);
    }

    await this.loadScript('jquery');
    
    // Ensure global variables are set
    if (window.jQuery && !window.$) {
      window.$ = window.jQuery;
    }
    
    return window.jQuery;
  }

  /**
   * Load Swiper only when carousel components are needed
   * @returns {Promise} Promise that resolves when Swiper is ready
   */
  async loadSwiper() {
    if (window.Swiper) {
      return Promise.resolve(window.Swiper);
    }

    await this.loadScript('swiper');
    return window.Swiper;
  }

  /**
   * Load GSAP only when animations are needed
   * @returns {Promise} Promise that resolves when GSAP is ready
   */
  async loadGSAP() {
    if (window.gsap) {
      return Promise.resolve(window.gsap);
    }

    await this.loadScript('gsap');
    return window.gsap;
  }

  /**
   * Check if a script is already loaded
   * @param {string} scriptName - Script name to check
   * @returns {boolean} True if script is loaded
   */
  isLoaded(scriptName) {
    return this.loadedScripts.has(scriptName);
  }

  /**
   * Preload scripts in the background for better UX
   * @param {Array} scriptNames - Array of script names to preload
   */
  async preloadScripts(scriptNames = []) {
    const preloadPromises = scriptNames.map(scriptName => {
      return this.loadScript(scriptName).catch(error => {
        console.warn(`Preload failed for ${scriptName}:`, error);
      });
    });

    await Promise.allSettled(preloadPromises);
  }

  /**
   * Check if component needs specific scripts based on its content
   * @param {HTMLElement} element - DOM element to scan
   * @returns {Array} Array of required script names
   */
  detectRequiredScripts(element) {
    const required = [];
    const content = element.innerHTML.toLowerCase();

    if (content.includes('parallax') || content.includes('jquery') || content.includes('$')) {
      required.push('jquery');
    }

    if (content.includes('swiper') || content.includes('carousel') || content.includes('splide')) {
      required.push('swiper');
    }

    if (content.includes('gsap') || content.includes('tweenlite') || content.includes('animation')) {
      required.push('gsap');
    }

    return required;
  }
}

// Create singleton instance
const scriptLoader = new ScriptLoader();

// Enhanced loading functions for backwards compatibility
window.loadJQuery = () => scriptLoader.loadJQuery();
window.loadSwiper = () => scriptLoader.loadSwiper();
window.loadGSAP = () => scriptLoader.loadGSAP();

export default scriptLoader;